import { Role } from "@/constants/rolePermissions";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

type RoleInput = Role | Role[] | undefined;

export const useUsers = (role?: RoleInput) => {
  return useQuery({
    queryKey: ["users", role],
    queryFn: async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) throw new Error("Usuario no autenticado");

        const rolesWithAssignments: Role[] = [
          "property_owner",
          "property_manager",
          "cleaning",
        ];

        const roleList = Array.isArray(role) ? role : role ? [role] : undefined;

        const shouldIncludeAssignments =
          roleList &&
          roleList.length === 1 &&
          rolesWithAssignments.includes(roleList[0]);

        if (shouldIncludeAssignments) {
          const roleValue = roleList[0];

          const { data: userProfiles, error: profilesError } = await supabase
            .from("user_profiles")
            .select("*")
            .eq("role", roleValue);

          if (profilesError) throw profilesError;

          const userIds = userProfiles.map((u) => u.user_id);

          const { data: assignments, error: assignmentsError } = await supabase
            .from("user_property_assignments")
            .select(
              `
              *,
              properties (
                id,
                title,
                short_name
              )
            `,
            )
            .in("user_id", userIds);

          if (assignmentsError) throw assignmentsError;

          const enriched = userProfiles.map((profile) => {
            const userAssignments = assignments.filter(
              (a) => a.user_id === profile.user_id,
            );

            return {
              ...profile,
              user_property_assignments: userAssignments,
            };
          });

          return enriched;
        }

        let query = supabase
          .from("user_profiles")
          .select("*")
          .order("created_at", { ascending: false });

        if (roleList && roleList.length > 0) {
          query = query.in("role", roleList);
        }

        const { data, error } = await query;

        if (error) throw error;

        return data || [];
      } catch (error) {
        console.error("Error in useUsers query:", error);
        throw error;
      }
    },
    enabled: true,
  });
};

export const useUserProperties = (userId: string) => {
  return useQuery({
    queryKey: ["user-properties", userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error("ID de usuario requerido");
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("Usuario no autenticado");
      }

      const { data, error } = await supabase
        .from("user_property_assignments")
        .select(
          `
          property_id,
          properties (
            id,
            title,
            short_name
          )
        `,
        )
        .eq("user_id", userId);

      if (error) {
        console.error("Error fetching user properties:", error);
        throw error;
      }

      return data;
    },
    enabled: !!userId,
  });
};
